/*
  GTH cart — perceived-speed layer.
  Added 13 Aug 2026. Revised same day: popdown, not drawer.

  WHY THIS EXISTS
  A Shopify cart write costs ~950ms on this shop (measured: server-timing
  processing 560-1060ms, of which an internal fetch is 540-790ms; a cart READ by
  comparison is ~150ms total). None of that is theme code and none of it can be
  removed from the theme. What the theme CAN stop doing is waiting in silence:
  before this file, nothing on screen moved between the click and the response.

  WHAT IT SHOWS
  The theme's own product popdown — the slim bar with the thumbnail, the item
  name and a View cart link. The full right-hand cart drawer covers too much of
  the page to be thrown at someone who only added one thing; the drawer now
  opens only when the customer asks for it, from the cart icon or from the
  popdown's own View cart link.

  This does not reimplement the popdown. It dispatches the theme's existing
  theme:cart:popdown event with detail.variant.id, which is the same path the
  theme uses itself; the theme renders ?section_id=api-product-popdown into
  [data-product-add-popdown-wrapper] and hides it again after five seconds. On
  the cart page the theme's own handler reloads the cart section instead, which
  is the correct behaviour there and needs no special case here.

  The point is WHEN it fires: at add-request START, so the ~200ms popdown render
  overlaps the ~950ms cart write instead of queueing behind it. Confirmation
  lands at ~200ms rather than ~1.2s.

  Hooking request-start rather than the click matters — a click on a quick-add
  that opens a variant chooser must not fire a confirmation, and by definition
  it never fires /cart/add. The click is used only as a source of context: the
  form it came from tells us the variant id when the request body is a FormData
  we cannot read (which is what the theme's own PDP form sends).

  Because it fires on request start, the bar can appear a moment before the add
  is actually confirmed. If the add fails, hide() pulls it straight back off.

  Also holds one shared cart snapshot. Before this, a single add read the same
  cart 3-5 times (theme.js, gth-cart.js, the free-item pass, Rebuy). Anything
  inside the theme can now call GthCart.read() and get the last snapshot if it
  is still fresh.

  Loaded from snippets/scoop-bag-auto-add.liquid, before gth-cart.js.
*/
(function () {
  if (window.__gthCartFast) return;
  window.__gthCartFast = true;

  var WRAPPER = '[data-product-add-popdown-wrapper]';
  var DEFAULT_MAX_AGE = 1500;

  var store = { cart: null, at: 0 };
  var lastForm = null;

  function variantFromForm(form) {
    if (!form) return null;
    var field = form.querySelector('[name="id"]');
    var value = field && field.value;
    return value && /^\d+$/.test(String(value)) ? String(value) : null;
  }

  function variantFromBody(init) {
    var body = init && init.body;
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) body = body.toString();
    if (typeof body !== 'string') return null;
    var match = body.match(/(?:^|[&{,])\s*"?id"?\s*[:=]\s*"?(\d+)/);
    return match ? match[1] : null;
  }

  function popdown(variantId) {
    if (!variantId) return false;
    if (!document.querySelector(WRAPPER)) return false;
    document.dispatchEvent(new CustomEvent('theme:cart:popdown', {
      detail: { variant: { id: Number(variantId) } },
      bubbles: true
    }));
    return true;
  }

  function hide() {
    var wrapper = document.querySelector(WRAPPER);
    if (wrapper) wrapper.classList.remove('is-visible');
  }

  var api = {
    popdown: popdown,
    hide: hide,
    end: hide,

    set: function (cart) {
      if (cart && typeof cart === 'object' && typeof cart.item_count === 'number') {
        store.cart = cart;
        store.at = Date.now();
      }
      return cart;
    },

    peek: function (maxAgeMs) {
      if (!store.cart) return null;
      var limit = maxAgeMs === undefined ? DEFAULT_MAX_AGE : maxAgeMs;
      if (Date.now() - store.at > limit) return null;
      return store.cart;
    },

    read: function (maxAgeMs, headers) {
      var cached = api.peek(maxAgeMs);
      if (cached) return Promise.resolve(cached);
      return fetch('/cart.js', { headers: headers || {} })
        .then(function (r) { return r.json(); })
        .then(api.set);
    },

    invalidate: function () {
      store.cart = null;
      store.at = 0;
    }
  };

  window.GthCart = api;

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target || !target.closest) return;
    var form = target.closest('form[action*="/cart/add"]');
    if (form) lastForm = form;
  }, true);

  function watch(url, init) {
    if (!url) return;
    var href = String(url);
    if (!/\/cart\/(add|change|update|clear)/.test(href)) return;
    api.invalidate();
    if (!/\/cart\/add/.test(href)) return;
    popdown(variantFromBody(init) || variantFromForm(lastForm));
  }

  var originalFetch = window.fetch;
  window.fetch = function (input, init) {
    try {
      watch(typeof input === 'string' ? input : (input && input.url) || '', init);
    } catch (e) {}
    return originalFetch.apply(this, arguments);
  };

  var originalOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    try { watch(url, null); } catch (e) {}
    return originalOpen.apply(this, arguments);
  };

  document.addEventListener('theme:cart:change', function (event) {
    if (event && event.detail && event.detail.cart) api.set(event.detail.cart);
  });

  document.addEventListener('theme:cart:error', hide);
})();
